import React, { useState, useEffect, useRef } from 'react';
import { Button } from "react-bootstrap";
import { FaCircleDot, FaMicrophone, FaRegCircleDot, FaTrash } from 'react-icons/fa6';
import CheckBox from './customCheckBox';
import MessagePopUp from './messagePopUp';
import { useTranslation } from 'react-i18next';

const AudioRecorder = ({ onAudioChange }) => {
    const [audioBlob, setAudioBlob] = useState(null);
    const [isRecording, setIsRecording] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const [mediaRecorder, setMediaRecorder] = useState(null);
    const [distortedAudioUrl, setDistortedAudioUrl] = useState(null);
    const [isDistorting, setIsDistorting] = useState(false);
    const [isDistortedChecked, setIsDistortedChecked] = useState(true);
    const [disableCheckWarning, setDisableCheckWarning] = useState(false); // Estado para manejar el mensaje emergente
    const timerRef = useRef(null);
    const maxRecordingTime = 120; // Tiempo máximo de grabación en segundos

    const [t] = useTranslation("publicIncident");

    useEffect(() => {
        let audioChunks = [];

        navigator.mediaDevices.getUserMedia({ audio: true })
            .then(stream => {
                const recorder = new MediaRecorder(stream);
                setMediaRecorder(recorder);

                recorder.ondataavailable = event => {
                    audioChunks.push(event.data);
                };

                recorder.onstop = () => {
                    const audioBlob = new Blob(audioChunks, { 'type': 'audio/wav' });
                    setAudioBlob(audioBlob);
                    audioChunks = [];
                };
            });

        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, []);

    useEffect(() => {
        if (audioBlob && !distortedAudioUrl && isDistortedChecked) {
            distortAudio(); // Distorsionar por defecto cuando hay un audioBlob
        } else if (isDistortedChecked && distortedAudioUrl) {
            onAudioChange(distortedAudioUrl); // Devolver el audio distorsionado
        } else if (audioBlob) {
            onAudioChange(URL.createObjectURL(audioBlob)); // Devolver el audio original
        }
    }, [isDistortedChecked, distortedAudioUrl, audioBlob]);

    const startRecording = () => {
        if (mediaRecorder) {
            // Restablecer el estado cuando se comienza a grabar
            setAudioBlob(null);
            setDistortedAudioUrl(null);
            setIsDistorting(false);
            setIsDistortedChecked(true); // Marcar el checkbox por defecto
            onAudioChange(null); // Devolver null ya que la grabación es nueva

            mediaRecorder.start();
            setIsRecording(true);
            setRecordingTime(0);

            timerRef.current = setInterval(() => {
                setRecordingTime(prevTime => {
                    if (prevTime + 1 >= maxRecordingTime) {
                        stopRecording(); // Detiene la grabación al llegar al tiempo máximo
                        return maxRecordingTime;
                    }
                    return prevTime + 1;
                });
            }, 1000);
        }
    };

    const stopRecording = () => {
        if (mediaRecorder) {
            mediaRecorder.stop();
            setIsRecording(false);
            if (timerRef.current) clearInterval(timerRef.current);
        }
    };

    const deleteRecording = () => {
        setAudioBlob(null);
        setRecordingTime(0);
        setDistortedAudioUrl(null);
        setIsDistorting(false);
        setIsDistortedChecked(false);
        onAudioChange(null); // Devolver null cuando se borra la grabación
    };

    const distortAudio = async (pitchShift = 0.8) => {
        if (!audioBlob) return;

        setIsDistorting(true); // Mostrar "Distorsionando..." antes de comenzar el proceso

        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const source = audioContext.createBufferSource();
        const arrayBuffer = await audioBlob.arrayBuffer();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

        // Crear un nuevo AudioBuffer con el mismo contenido
        const newBuffer = audioContext.createBuffer(
            audioBuffer.numberOfChannels,
            audioBuffer.length,
            audioBuffer.sampleRate
        );

        // Copiar los datos de los canales al nuevo buffer
        for (let channel = 0; channel < audioBuffer.numberOfChannels; channel++) {
            newBuffer.copyToChannel(audioBuffer.getChannelData(channel), channel);
        }

        source.buffer = newBuffer;

        // Ajuste del pitch (tono) utilizando playbackRate
        source.playbackRate.value = pitchShift; // pitchShift < 1 baja el pitch, pitchShift > 1 lo sube

        const destination = audioContext.createMediaStreamDestination();
        source.connect(destination);
        source.start();

        const mediaRecorder = new MediaRecorder(destination.stream);
        const chunks = [];

        mediaRecorder.ondataavailable = (e) => chunks.push(e.data);
        mediaRecorder.onstop = () => {
            const newBlob = new Blob(chunks, { type: 'audio/wav' });
            setDistortedAudioUrl(URL.createObjectURL(newBlob));
            setIsDistorting(false); // Ocultar "Distorsionando..." cuando termine
            onAudioChange(URL.createObjectURL(newBlob)); // Devolver el audio distorsionado
        };

        mediaRecorder.start();
        source.onended = () => mediaRecorder.stop();
    };

    const handleDistortionCheck = (checked) => {
        if (!checked && isDistortedChecked) {
            // Mostrar mensaje de advertencia si intentan desmarcar el checkbox
            setDisableCheckWarning(true);
            return;
        }

        if (!distortedAudioUrl && checked) {
            distortAudio(); // Distorsionar la primera vez que se selecciona
        }
        setIsDistortedChecked(checked);
    };

    const handleDisableCheck = () => {
        setIsDistortedChecked(false);
        setDisableCheckWarning(false);
    };

    const formatTime = (time) => {
        const minutes = Math.floor(time / 60);
        const seconds = time % 60;
        return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    };

    return (
        <>
            <MessagePopUp
                show={disableCheckWarning}
                handleClose={() => setDisableCheckWarning(false)}
                title={t("audioDisablePopUp.messageCantDisableTitle")}
                textBody={t("audioDisablePopUp.messageCantDisableText")}
                buttonText={t("audioDisablePopUp.acceptButton")}
                onClickButton={handleDisableCheck}
            />

            <div className="d-flex flex-column align-items-center" >
                {audioBlob && !isDistorting && (
                    <>
                        <div class="mb-2 d-flex flex-row align-items-center justify-content-between text-center">

                            <audio className=' max-width-80' controls src={isDistortedChecked && distortedAudioUrl ? distortedAudioUrl : URL.createObjectURL(audioBlob)} controlsList="nodownload" >{/* controlsList="nodownload" */}
                                {t("audioInput.notSupported")}
                            </audio>
                            <Button
                                variant="danger"
                                className="custom-button-danger"
                                onClick={deleteRecording}
                            >
                                <FaTrash size={20} />
                            </Button>

                        </div>

                        <span className='w-100 mb-3' style={{ marginTop: '-1em' }}>
                            <CheckBox
                                onChange={handleDistortionCheck}
                                initialChecked={isDistortedChecked}
                                text={t("audioInput.distortion")}
                                bigCheck={true}
                            />

                        </span>

                    </>
                )}
                {isDistorting && <p className="mb-2" >{t("audioInput.distortionRunning")}</p>}
                {isRecording &&
                    <div>
                        <p className="mb-2 text-center font-bold fs-15">{formatTime(recordingTime)}</p>
                        <p className="mb-2">{t("audioInput.maxtime")}</p>
                    </div>
                }
                <Button
                    variant="secondary"
                    className="custom-button-secondary"
                    onClick={isRecording ? stopRecording : startRecording}
                >
                    {isRecording ? <FaMicrophone size={22} className='audio-recording-icon ' /> : <FaRegCircleDot size={20} style={{ color: 'var(--dark-red)' }} />}
                    {isRecording ? <span style={{ marginLeft: '0.5em' }}>{t("audioInput.recording")}</span> : <span style={{ marginLeft: '0.5em' }}>{t("audioInput.record")}</span>}
                </Button>
            </div>
        </>
    );
}

export default AudioRecorder;
